import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { SEO } from "@/components/SEO";
import { StructuredData } from "@/components/StructuredData";
import { supabase } from "@/integrations/supabase/client";
import { buildPublicAppUrl } from "@/lib/appConfig";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { sanitizeRichTextHtml, stripRichTextToPlainText } from "@/lib/richText";

interface BlogPostRecord {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  meta_title: string | null;
  meta_description: string | null;
  cover_image_url: string | null;
  author_name: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

interface BlogFaqRecord {
  id: string;
  question: string;
  answer: string;
  sort_order: number;
}

function estimateReadTime(content: string) {
  const words = stripRichTextToPlainText(content).trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPostRecord | null>(null);
  const [faqs, setFaqs] = useState<BlogFaqRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const { data: postData, error: postError } = await supabase
          .from("blog_posts")
          .select("*")
          .eq("slug", slug)
          .eq("published", true)
          .maybeSingle();

        if (postError) throw postError;
        setPost((postData as BlogPostRecord | null) || null);

        if (!postData?.id) {
          setFaqs([]);
          return;
        }

        const { data: faqData, error: faqError } = await supabase
          .from("blog_post_faqs")
          .select("id, question, answer, sort_order")
          .eq("blog_post_id", postData.id)
          .order("sort_order", { ascending: true });

        if (faqError) throw faqError;
        setFaqs((faqData as BlogFaqRecord[]) || []);
      } catch (error) {
        console.error("Failed to load blog post", error);
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  const renderedContent = useMemo(() => sanitizeRichTextHtml(post?.content || ""), [post?.content]);

  const articleSchema = useMemo(() => {
    if (!post) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.meta_description || post.excerpt || post.title,
      image: post.cover_image_url || buildPublicAppUrl("/og-image.png"),
      datePublished: post.published_at || post.created_at,
      dateModified: post.updated_at,
      author: {
        "@type": "Organization",
        name: post.author_name || "Splitkart",
        url: buildPublicAppUrl("/blog"),
      },
      publisher: {
        "@type": "Organization",
        name: "Splitkart",
        url: buildPublicAppUrl("/"),
        logo: {
          "@type": "ImageObject",
          url: buildPublicAppUrl("/favicon.png?v=6"),
        },
      },
      articleSection: "Split payments and group gifting",
      keywords: "group gifting, split payments, Shopify, WooCommerce, ecommerce",
      mainEntityOfPage: buildPublicAppUrl(`/blog/${post.slug}`),
    };
  }, [post]);

  const faqSchema = useMemo(() => {
    if (faqs.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };
  }, [faqs]);

  const breadcrumbSchema = useMemo(() => {
    if (!post) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: buildPublicAppUrl("/"),
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: buildPublicAppUrl("/blog"),
        },
        {
          "@type": "ListItem",
          position: 3,
          name: post.title,
          item: buildPublicAppUrl(`/blog/${post.slug}`),
        },
      ],
    };
  }, [post]);

  if (loading) {
    return (
      <SiteLayout>
        <div className="container-tight flex min-h-[50vh] items-center justify-center">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading article...
          </div>
        </div>
      </SiteLayout>
    );
  }

  if (!post) {
    return (
      <SiteLayout>
        <SEO
          title="Blog Post Not Found"
          description="The requested Splitkart article could not be found."
          url={`/blog/${slug || ""}`}
          noIndex
        />
        <div className="container-tight py-24 text-center">
          <h1 className="text-3xl font-bold">Blog post not found</h1>
          <p className="mt-3 text-muted-foreground">
            This article is missing, unpublished, or the slug changed.
          </p>
          <Link
            to="/blog"
            className="mt-8 inline-flex items-center rounded-full border border-border px-5 py-3 text-sm font-semibold transition-colors hover:border-primary/40 hover:text-primary"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to blog
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <SEO
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt || post.title}
        image={post.cover_image_url || undefined}
        url={`/blog/${post.slug}`}
        type="article"
      />
      {articleSchema && <StructuredData data={articleSchema} />}
      {faqSchema && <StructuredData data={faqSchema} />}
      {breadcrumbSchema && <StructuredData data={breadcrumbSchema} />}

      <article className="pb-24">
        <section className="section bg-gradient-hero">
          <div className="container-tight max-w-4xl">
            <Link
              to="/blog"
              className="inline-flex items-center text-sm font-semibold text-primary transition-colors hover:text-primary/80"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to blog
            </Link>

            <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span>{post.author_name || "Splitkart"}</span>
              <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
              <span>{estimateReadTime(post.content)} min read</span>
            </div>

            <h1 className="mt-5 max-w-3xl text-balance text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
              {post.title}
            </h1>
            {(post.excerpt || post.meta_description) && (
              <p className="mt-5 max-w-3xl text-lg text-muted-foreground">
                {post.excerpt || post.meta_description}
              </p>
            )}
          </div>
        </section>

        <div className="container-tight max-w-4xl">
          {post.cover_image_url && (
            <div className="-mt-10 overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
              <img
                src={post.cover_image_url}
                alt={post.title}
                width="1200"
                height="750"
                className="h-auto w-full object-cover"
              />
            </div>
          )}

          <div className="mt-10 overflow-x-auto rounded-3xl border border-border bg-card p-8 shadow-soft md:p-10">
            <h2 className="sr-only">Article content</h2>
            <div
              className="prose prose-lg max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-img:rounded-2xl prose-img:shadow-soft prose-table:my-8 prose-table:w-full prose-table:min-w-[680px] prose-table:border-collapse prose-th:border prose-th:border-border prose-th:bg-muted prose-th:p-4 prose-th:text-left prose-th:font-semibold prose-td:border prose-td:border-border prose-td:p-4 prose-td:align-top"
              dangerouslySetInnerHTML={{ __html: renderedContent }}
            />
          </div>

          {faqs.length > 0 && (
            <section className="mt-12 rounded-3xl border border-border bg-card p-8 shadow-soft md:p-10">
              <h2 className="text-2xl font-bold">FAQs</h2>
              <p className="mt-2 text-muted-foreground">
                Answers related to this article, also marked up for search engines.
              </p>
              <Accordion type="single" collapsible className="mt-6">
                {faqs.map((faq) => (
                  <AccordionItem key={faq.id} value={faq.id}>
                    <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}
        </div>
      </article>
    </SiteLayout>
  );
}

export default BlogPost;
